/**
 * Push Notification Service
 * Handles native push notifications for iOS using Capacitor
 */

import { logger } from './logger';

// Storage key for device token
const DEVICE_TOKEN_KEY = 'repconnect_device_token';
const NOTIFICATIONS_ENABLED_KEY = 'repconnect_notifications_enabled';

class PushNotificationService {
  constructor() {
    this.PushNotifications = null;
    this.isNativeApp = false;
    this.initialized = false;
    this.deviceToken = null;
    this.listeners = [];
  }

  /**
   * Initialize the push notification service
   * Must be called after app loads
   */
  async initialize() {
    if (this.initialized) return;

    try {
      // Check if running in Capacitor native app
      this.isNativeApp = window.Capacitor &&
        window.Capacitor.isNativePlatform &&
        window.Capacitor.isNativePlatform();

      if (this.isNativeApp) {
        // Dynamically import the plugin only in native context
        const { PushNotifications } = await import('@capacitor/push-notifications');
        this.PushNotifications = PushNotifications;

        // Set up listeners
        await this.setupListeners();

        logger.debug('Push notification service initialized for native app');
      } else {
        logger.debug('Push notification service: Not a native app, push disabled');
      }

      this.initialized = true;
    } catch (error) {
      logger.error('Failed to initialize push notification service:', error);
      this.initialized = true;
    }
  }

  /**
   * Set up push notification event listeners
   */
  async setupListeners() {
    if (!this.PushNotifications) return;

    // On registration success
    await this.PushNotifications.addListener('registration', (token) => {
      logger.debug('Push registration success, token:', token.value);
      this.deviceToken = token.value;
      localStorage.setItem(DEVICE_TOKEN_KEY, token.value);

      // Notify any registered callbacks
      this.listeners.forEach(listener => {
        if (listener.event === 'registration') {
          listener.callback(token.value);
        }
      });
    });

    // On registration error
    await this.PushNotifications.addListener('registrationError', (error) => {
      logger.error('Push registration error:', error);
    });

    // On push notification received (app in foreground)
    await this.PushNotifications.addListener('pushNotificationReceived', (notification) => {
      logger.debug('Push notification received:', notification);

      // Notify any registered callbacks
      this.listeners.forEach(listener => {
        if (listener.event === 'received') {
          listener.callback(notification);
        }
      });
    });

    // On push notification action performed (user tapped notification)
    await this.PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      logger.debug('Push notification action performed:', action);

      // Notify any registered callbacks
      this.listeners.forEach(listener => {
        if (listener.event === 'actionPerformed') {
          listener.callback(action);
        }
      });
    });
  }

  /**
   * Request permission and register for push notifications
   * @param {string} userId - The user's ID to associate with the device token
   * @returns {Promise<{success: boolean, token?: string, error?: string}>}
   */
  async requestPermission(userId) {
    await this.initialize();

    logger.debug('requestPermission called', {
      userId,
      isNativeApp: this.isNativeApp,
      hasPushNotifications: !!this.PushNotifications
    });

    if (!this.isNativeApp || !this.PushNotifications) {
      logger.error('Push not available', { isNativeApp: this.isNativeApp, hasPushNotifications: !!this.PushNotifications });
      return { success: false, error: 'Push notifications not available' };
    }

    try {
      // Check current permission status
      logger.debug('Checking permissions...');
      let permStatus = await this.PushNotifications.checkPermissions();
      logger.debug('Permission status:', permStatus);

      if (permStatus.receive === 'prompt') {
        // Request permission
        permStatus = await this.PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        return { success: false, error: 'Push notification permission denied' };
      }

      // Register with APNs
      await this.PushNotifications.register();

      // Wait for token (it comes via the registration listener)
      // Give it a moment to receive the token
      await new Promise(resolve => setTimeout(resolve, 1000));

      const token = this.deviceToken || localStorage.getItem(DEVICE_TOKEN_KEY);

      if (token && userId) {
        // Save token to server
        await this.saveTokenToServer(userId, token);
      }

      localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, 'true');

      return { success: true, token };
    } catch (error) {
      logger.error('Error requesting push permission:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save device token to server for sending notifications
   * @param {string} userId
   * @param {string} token
   */
  async saveTokenToServer(userId, token) {
    try {
      // In Capacitor apps, always use full URL since we're not on the same domain
      const isCapacitor = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
      const API_URL = isCapacitor || window.location.hostname === 'localhost'
        ? 'https://login.meetwithreps.com/api'
        : '/api';

      logger.debug('Saving token to server:', { API_URL, userId, tokenPrefix: token.substring(0, 20) });

      const response = await fetch(`${API_URL}/register-device`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          token,
          platform: window.Capacitor?.getPlatform?.() || 'ios'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to register device token');
      }

      logger.debug('Device token saved to server');
      return true;
    } catch (error) {
      logger.error('Error saving device token to server:', error);
      return false;
    }
  }

  /**
   * Check if push notifications are enabled
   * @returns {Promise<boolean>}
   */
  async areNotificationsEnabled() {
    await this.initialize();

    if (!this.isNativeApp || !this.PushNotifications) {
      return false;
    }

    try {
      const permStatus = await this.PushNotifications.checkPermissions();
      return permStatus.receive === 'granted';
    } catch (error) {
      logger.error('Error checking notification permissions:', error);
      return false;
    }
  }

  /**
   * Get the stored device token
   * @returns {string|null}
   */
  getDeviceToken() {
    return this.deviceToken || localStorage.getItem(DEVICE_TOKEN_KEY);
  }

  /**
   * Check if this is a native app that supports push
   * @returns {boolean}
   */
  isSupported() {
    return this.isNativeApp && this.PushNotifications !== null;
  }

  /**
   * Add a listener for push notification events
   * @param {string} event - 'registration', 'received', or 'actionPerformed'
   * @param {Function} callback
   */
  addListener(event, callback) {
    this.listeners.push({ event, callback });
  }

  /**
   * Remove a listener
   * @param {string} event
   * @param {Function} callback
   */
  removeListener(event, callback) {
    this.listeners = this.listeners.filter(
      l => !(l.event === event && l.callback === callback)
    );
  }

  /**
   * Clear the badge count
   */
  async clearBadge() {
    await this.initialize();

    if (!this.isNativeApp || !this.PushNotifications) {
      return;
    }

    try {
      // Badge is typically managed server-side with APNs
      // For local badge clearing, we need the Badge plugin
      // This is handled via the notification payload from server
      logger.debug('Badge clear requested');
    } catch (error) {
      logger.error('Error clearing badge:', error);
    }
  }

  /**
   * Disable push notifications for this device
   * @param {string} userId
   */
  async disableNotifications(userId) {
    try {
      const token = this.getDeviceToken();

      if (token && userId) {
        // Tell server to stop sending notifications to this device
        const isCapacitor = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
        const API_URL = isCapacitor || window.location.hostname === 'localhost'
          ? 'https://login.meetwithreps.com/api'
          : '/api';

        await fetch(`${API_URL}/unregister-device`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId, token })
        });
      }

      localStorage.removeItem(DEVICE_TOKEN_KEY);
      localStorage.removeItem(NOTIFICATIONS_ENABLED_KEY);

      logger.debug('Push notifications disabled');
      return { success: true };
    } catch (error) {
      logger.error('Error disabling notifications:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
